import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'
import { registerCertificateHandler as registerRootEntryHandler } from '@unbridaled/search-sdk'
import { registerCertificateHandler as registerSubpathEntryHandler } from '@unbridaled/search-sdk/Certificate'
import { getCertificateAction } from 'utils/certificateViewer'

const OVERLAY_STYLE: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'rgba(0,0,0,0.5)',
  zIndex: 2147483647,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}

const CLOSE_BUTTON_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: '10px',
  right: '10px',
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '40px',
  height: '40px',
  padding: 0,
  border: 0,
  borderRadius: '50%',
  background: '#fff',
  cursor: 'pointer'
}

const VIEWER_FRAME_STYLE: React.CSSProperties = {
  display: 'block',
  width: '100%',
  height: '100%',
  border: 0
}

/**
 * Owns certificate display for every search-sdk `showCertificate` call, the same way console-ui
 * does (CertificateViewerRoot): registering a handler makes the SDK defer to this host instead of
 * mounting its own standalone modal, which relies on unprefixed Tailwind classes this host's
 * stylesheet does not ship. The look is the SDK modal's: a dimmed full-screen overlay with the
 * certificate and a round close button in the top-right corner, styled inline. Mount once, inside
 * the app's i18n context.
 */
export const CertificateViewer: React.FC = () => {
  const { t } = useTranslation()
  const [src, setSrc] = useState<string | null>(null)

  useEffect(() => {
    const handler = ({ uri, viewerUrl }: { uri: string; viewerUrl?: string }): void => {
      const action = getCertificateAction(uri, viewerUrl ?? process.env.VIEWER_URL)
      if (action.kind === 'window') {
        window.open(action.url)
      } else {
        setSrc(action.src)
      }
    }
    // search-sdk keeps the handler in module state, and its root entry (a self-contained bundle)
    // and its per-page subpath entries (ProductDetailsPage, ...) each ship their own copy of it.
    // This app renders SDK pages from both, so the handler has to be registered on both.
    registerRootEntryHandler(handler)
    registerSubpathEntryHandler(handler)
    return (): void => {
      registerRootEntryHandler(null)
      registerSubpathEntryHandler(null)
    }
  }, [])

  useEffect(() => {
    if (!src) return undefined
    // The viewer's /cert page posts this to its parent when the user clicks the document.
    const onMessage = (event: MessageEvent): void => {
      if (event.data === 'ubCertificateViewerClosed') setSrc(null)
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setSrc(null)
    }
    window.addEventListener('message', onMessage)
    window.addEventListener('keydown', onKeyDown)
    return (): void => {
      window.removeEventListener('message', onMessage)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [src])

  if (!src) return null

  return ReactDOM.createPortal(
    <div style={OVERLAY_STYLE} onClick={(): void => setSrc(null)}>
      <button type="button" aria-label={t('common.close')} onClick={(): void => setSrc(null)} style={CLOSE_BUTTON_STYLE}>
        <svg viewBox="0 0 24 24" fill="none" width="16" height="16" aria-hidden="true">
          <path
            fill="#000"
            d="M10.586 12 2.793 4.207l1.414-1.414L12 10.586l7.793-7.793 1.414 1.414L13.414 12l7.793 7.793-1.414 1.414L12 13.414l-7.793 7.793-1.414-1.414L10.586 12Z"
          />
        </svg>
      </button>
      <iframe title={t('certificateModal.title')} src={src} style={VIEWER_FRAME_STYLE} />
    </div>,
    document.body
  )
}

export default CertificateViewer
